import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2Icon, PauseIcon, PlayIcon, ShieldAlertIcon } from 'lucide-react';
import {
  account,
  app,
  BridgeError,
  bridge,
  migration as migrationApi,
  type DataTypeCatalog,
  type MigrationSnapshot,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { StepCard } from '@/components/StepCard';
import { InfoCard, TaskProgressCard, taskStatusLabel } from '@/components/task-ui';
import { PageSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD } from '@/components/ui';

/**
 * 薯薯全能通 · 笔记迁移：把笔记重新发布到同平台的另一个账号（点赞 / 收藏 / 关注不做）。
 *
 * 流程照搬搬小音 `pages/bxy/MigrationPage.tsx` 的四步：源账号 → 数据类型 → 目标账号 → 确认开始，
 * 状态（补拉 / 迁移中 / 暂停 / 门禁拦截 / 完成 / 失败）也沿用它的 `migration.*` 语义。区别：
 * - 可迁移类型由原生 `migration.types` 按源账号平台下发（目前只有笔记），`supported=false` 时只能看不能开跑；
 * - 只能在**同一平台**的账号之间迁移（小红书 → 小红书）；
 * - 首版不做逐类型筛选（条数上限 / 时间范围）。
 */

const STATUS_LABELS: Record<string, string> = {
  preparing: '迁移前补拉中',
  running: '迁移中',
};

export function ShushuMigrationPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
  } = useAccounts();
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<DataTypeCatalog | null>(null);
  /** App 版本太旧、没有 migration.types */
  const [outdated, setOutdated] = useState(false);
  const [types, setTypes] = useState<string[]>([]);
  const [targetId, setTargetId] = useState('');
  const [snapshot, setSnapshot] = useState<MigrationSnapshot | null>(null);
  const [busy, setBusy] = useState(false);

  const source = accounts.find((a) => a.id === selectedId) ?? null;
  const target = accounts.find((a) => a.id === targetId) ?? null;
  // 首屏要等类型目录到位（或已出错）再撤骨架
  const loaded =
    !infoLoading && !accountsLoading && (!source || catalog !== null || outdated || error !== null);
  useAppReady(loaded);
  useGroupedSurface();

  // 进度用事件推送，不轮询（PROTOCOL.md §5）
  useEffect(
    () => bridge.on('migration.progress', (payload) => setSnapshot(payload as MigrationSnapshot)),
    [],
  );

  // 可迁移类型：随源账号（平台）变
  useEffect(() => {
    setCatalog(null);
    setTypes([]);
    setSnapshot(null);
    if (!source) return;
    let cancelled = false;
    migrationApi
      .types(source.id)
      .then((next) => {
        if (!cancelled) setCatalog(next);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof BridgeError && e.code === 'unknown_method') setOutdated(true);
        else setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [source?.id]);

  const supported = catalog?.supported ?? false;

  // 支持时才有任务状态可拉
  useEffect(() => {
    if (!source || !supported) return;
    let cancelled = false;
    migrationApi
      .state(source.id)
      .then((next) => {
        if (!cancelled) setSnapshot((prev) => prev ?? next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [source?.id, supported]);

  const state = snapshot;
  const preparing = state?.state === 'preparing';
  const active = preparing || state?.state === 'running' || state?.state === 'paused';
  const completed = state?.state === 'completed';
  const failed = state?.state === 'failed';
  const blocked = state?.state === 'blocked';
  const counts = state?.counts;

  /** 目标账号：同平台、登录有效、不是源账号。 */
  const isEligibleTarget = (a: (typeof accounts)[number]) =>
    a.canWrite && a.id !== source?.id && a.platform === source?.platform;
  const eligibleTargets = accounts.filter(isEligibleTarget);

  const step1Done = !!source;
  const step2Done = types.length > 0;
  const step3Done = !!target && isEligibleTarget(target);

  const typeDefs = catalog?.types ?? [];
  const labelOf = (key: string) => typeDefs.find((t) => t.key === key)?.label ?? key;

  const pickSource = useCallback(async () => {
    try {
      const picked = await account.pick('all');
      if (picked.id === targetId) {
        setError('源账号和目标账号不能相同，请另选一个源账号');
        return;
      }
      setSelectedId(picked.id);
      setTargetId('');
      setError(null);
    } catch {
      // 用户取消
    }
  }, [setSelectedId, targetId]);

  const pickTarget = useCallback(async () => {
    try {
      const picked = await account.pick('authenticated');
      if (picked.id === source?.id) {
        setError('目标账号不能和源账号相同');
        return;
      }
      if (picked.platform !== source?.platform) {
        setError(`只能迁移到同一平台的账号，请选择${source?.platformLabel ?? '同平台'}账号`);
        return;
      }
      setTargetId(picked.id);
      setError(null);
    } catch {
      // 用户取消
    }
  }, [source]);

  const toggleType = useCallback(
    (key: string) => {
      if (busy || active || !supported) return;
      setTypes((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]));
    },
    [active, busy, supported],
  );

  const run = useCallback(
    async (action: 'start' | 'pause' | 'resume', force = false) => {
      if (!source || busy) return;
      if (action === 'start' && !target) return;
      setBusy(true);
      setError(null);
      try {
        const next =
          action === 'start'
            ? await migrationApi.start(source.id, target!.id, types, false, undefined, force)
            : action === 'pause'
              ? await migrationApi.pause(source.id)
              : await migrationApi.resume(source.id);
        // 事件可能先到：别用刚返回的 preparing 把已推进的状态盖回去
        setSnapshot((prev) =>
          next.state === 'preparing' && prev?.state && prev.state !== 'idle' ? prev : next,
        );
      } catch (e) {
        setError(describeError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, source, target, types],
  );

  const reset = useCallback(() => {
    setSnapshot(null);
    setError(null);
    setTypes([]);
    setTargetId('');
  }, []);

  const ratio = counts && counts.total > 0 ? Math.min(1, counts.migrated / counts.total) : undefined;

  return (
    <PageShell title="账号迁移" description="把笔记重新发布到同平台的另一个账号">
      {!isNative && loaded ? (
        <Card className={CARD}>
          <p className="text-muted-foreground text-xs">
            当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
          </p>
        </Card>
      ) : null}

      {loaded && isNative && accounts.length === 0 ? (
        <InfoCard title="没有可用的账号" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          迁移需要一个源账号和一个同平台、已登录的目标账号，请先在 App 内登录。
        </InfoCard>
      ) : null}

      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持迁移，请更新到最新版本后再试。
        </InfoCard>
      ) : null}

      {catalog && !supported ? (
        <InfoCard title="即将支持" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          {catalog.note ?? `${source?.platformLabel ?? '该平台'}的迁移正在开发中，敬请期待。`}
        </InfoCard>
      ) : null}

      {/* 进行中：补拉 / 迁移 / 暂停 */}
      {active && state ? (
        <TaskProgressCard
          title={taskStatusLabel(state.state, STATUS_LABELS) ?? state.state}
          right={counts ? `${counts.migrated} / ${counts.total}` : undefined}
          ratio={preparing ? undefined : ratio}
          paused={state.state === 'paused'}
          detail={
            preparing
              ? (state.note ?? '正在补拉源账号的最新数据…')
              : counts && counts.failed > 0
                ? `失败 ${counts.failed} 条`
                : undefined
          }
          current={preparing ? state.prefetch?.item : state.current}
          action={
            state.state === 'paused' ? (
              <Button className="w-full" loading={busy} onClick={() => void run('resume')}>
                <PlayIcon data-icon="inline-start" />
                继续迁移
              </Button>
            ) : (
              <Button variant="secondary" className="w-full" loading={busy} onClick={() => void run('pause')}>
                <PauseIcon data-icon="inline-start" />
                暂停
              </Button>
            )
          }
        />
      ) : null}

      {/* 门禁拦截：目标账号试写没写进去，疑似风控 */}
      {blocked ? (
        <InfoCard
          title="目标账号暂时无法写入"
          icon={<ShieldAlertIcon className="text-destructive size-5 shrink-0" />}
          action={
            <div className="flex flex-col gap-2">
              <Button className="w-full" loading={busy} onClick={() => void run('start', true)}>
                <PlayIcon data-icon="inline-start" />
                仍要尝试迁移
              </Button>
              <Button variant="secondary" className="w-full" onClick={reset}>
                重新选择
              </Button>
            </div>
          }
        >
          {state?.note ?? '目标账号试写失败，可能处于风控中。建议稍等一段时间再试。'}
        </InfoCard>
      ) : null}

      {completed ? (
        <Card className={CARD}>
          <div className="flex flex-col items-center py-5 text-center">
            <CheckCircle2Icon className="text-primary size-14" />
            <strong className="text-title3 mt-3 font-semibold">迁移完成</strong>
            <p className="text-muted-foreground mt-1 text-sm tabular-nums">
              已迁移 {counts?.migrated ?? 0} / {counts?.total ?? 0} 条
              {counts && counts.failed > 0 ? ` · 失败 ${counts.failed} 条` : ''}
            </p>
            {state?.selectedTypes?.length ? (
              <p className="text-caption1 text-muted-foreground mt-1">
                迁移内容：{state.selectedTypes.map(labelOf).join('、')}
              </p>
            ) : null}
            <div className="mt-4 flex w-full flex-col gap-2">
              <Button className="w-full" onClick={() => void app.close()}>
                回到主页
              </Button>
              <Button variant="secondary" className="w-full" onClick={reset}>
                再迁移一次
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {failed ? (
        <InfoCard
          title={taskStatusLabel('failed') ?? '迁移失败'}
          icon={<ShieldAlertIcon className="text-destructive size-5 shrink-0" />}
          action={
            <div className="flex flex-col gap-2">
              <Button className="w-full" loading={busy} onClick={() => void run('resume')}>
                <PlayIcon data-icon="inline-start" />
                继续迁移
              </Button>
              <Button variant="secondary" className="w-full" onClick={reset}>
                重新开始
              </Button>
            </div>
          }
        >
          {state?.error ? describeError(state.error) : '目标账号若已退出登录，请重新登录后点「继续迁移」。'}
        </InfoCard>
      ) : null}

      {!active && !completed && !failed && !blocked && loaded && accounts.length > 0 ? (
        <>
          <StepCard
            number={1}
            title="源账号"
            subtitle={step1Done ? '点击可重新选择' : '从这个账号迁出（不必保持登录）'}
            state={step1Done ? 'complete' : 'active'}
          >
            <AccountCard
              account={source}
              label={source ? [source.platformLabel, source.handle].filter(Boolean).join(' · ') : undefined}
              showId={false}
              emptyText="选择源账号"
              onClick={() => void pickSource()}
              flat
            />
          </StepCard>

          {step1Done && catalog ? (
            <StepCard
              number={2}
              title="选择要迁移的数据"
              subtitle={step2Done ? `已选 ${types.length} 类` : '至少选择一种'}
              state={step2Done ? 'complete' : 'active'}
            >
              <div className="flex flex-col gap-2">
                {typeDefs.map((def) => (
                  <SelectRow
                    key={def.key}
                    checked={types.includes(def.key)}
                    onCheckedChange={() => toggleType(def.key)}
                    disabled={!supported}
                    detail={def.count != null ? `${def.count} 条` : undefined}
                  >
                    {def.label}
                  </SelectRow>
                ))}
              </div>
              <p className="text-caption1 text-muted-foreground mt-2 leading-snug">
                数字为本机已备份条数；开始迁移时会先自动补拉源账号的最新笔记。
              </p>
            </StepCard>
          ) : null}

          {step1Done && step2Done ? (
            <StepCard
              number={3}
              title="目标账号"
              subtitle={step3Done ? '点击可重新选择' : `迁到这里（需已登录的${source?.platformLabel ?? '同平台'}账号）`}
              state={step3Done ? 'complete' : 'active'}
            >
              {eligibleTargets.length > 0 ? (
                <AccountCard
                  account={target}
                  label={target ? [target.platformLabel, target.handle].filter(Boolean).join(' · ') : undefined}
                  showId={false}
                  emptyText="选择目标账号"
                  onClick={() => void pickTarget()}
                  flat
                />
              ) : (
                <p className="text-muted-foreground text-xs leading-snug">
                  还没有可用的目标账号。回到 App 主页，点左上角账号 → 添加账号，登录另一个
                  {source?.platformLabel ?? '同平台'}账号后再来。
                </p>
              )}
            </StepCard>
          ) : null}

          {step1Done && step2Done && step3Done ? (
            <StepCard number={4} title="确认并开始" subtitle="先补拉源账号的笔记，再逐篇发布到目标账号" state="active">
              <Button className="w-full" loading={busy} onClick={() => void run('start')}>
                {!busy ? <PlayIcon data-icon="inline-start" /> : null}
                {busy ? '处理中…' : '开始迁移'}
              </Button>
            </StepCard>
          ) : null}
        </>
      ) : null}

      {(error ?? accountError) ? <p className="text-destructive px-1 text-sm">{error ?? accountError}</p> : null}

      {!loaded ? <PageSkeleton kind="migration" /> : null}
    </PageShell>
  );
}
